/**
 * AI Tokens Consumer Portal — API Client
 *
 * HTTP client for calling the token-kit API backend with Consumer Cognito JWT
 * authentication. Handles token attachment, error mapping, and 401 logout.
 */

import { appConfig } from '../config';

/** Provides the current Cognito ID token */
let getIdToken: (() => string | null) | null = null;

/**
 * Register the token provider (called by auth service on init).
 * Avoids a circular dependency between api-client and auth.
 */
export function setTokenProvider(provider: () => string | null): void {
  getIdToken = provider;
}

/**
 * Build the full API URL for a path
 */
function buildUrl(path: string, queryParams?: Record<string, string>): string {
  const base = `${appConfig.apiUrl}/${appConfig.apiVersion}${path}`;
  if (!queryParams) return base;
  const params = new URLSearchParams(queryParams);
  return `${base}?${params.toString()}`;
}

/** Standard API response shape from the backend */
interface BackendResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string } | string;
  meta?: { requestId: string; timestamp: string };
}

/**
 * Make an authenticated API request
 */
async function request<T>(
  method: string,
  path: string,
  options?: {
    body?: unknown;
    query?: Record<string, string>;
  }
): Promise<T> {
  const url = buildUrl(path, options?.query);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Attach Cognito ID token if available
  const token = getIdToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (options?.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, fetchOptions);

  // Handle 401 — token expired or invalid
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new Error('Authentication expired. Please sign in again.');
  }

  const data: BackendResponse<T> = await response.json();

  if (!data.success) {
    const err = data.error;
    const errorMsg = typeof err === 'object' ? err.message : (err || 'Request failed');
    throw new Error(errorMsg);
  }

  return data.data as T;
}

// ── Convenience methods ──────────────────────────────────────────

export const api = {
  get: <T>(path: string, query?: Record<string, string>) =>
    request<T>('GET', path, { query }),

  post: <T>(path: string, body?: unknown) =>
    request<T>('POST', path, { body }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>('PATCH', path, { body }),

  delete: <T>(path: string) =>
    request<T>('DELETE', path),
};
